import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useReducer
} from "react";
import { useCookies } from "react-cookie";
import { graphql, useStaticQuery } from "gatsby";

import CookieContext from "../CookieContext";

import { CookieBanner } from "./CookieBanner/CookieBanner";
import { CookieFunktions } from "./CookieFunctions/CookieFunktions";

const appLang = process.env.GATSBY_APP_LANG;

export function CookieConsent({ groups, reopened, setReopened }) {
    const [cookies, setCookie, removeCookie] = useCookies([]);

    const {
        allDatoCmsLanguageConfiguration: { nodes: languageConfigs }
    } = useStaticQuery(query);

    const [configData, filteredGroups] = useMemo(() => {
        const config = languageConfigs.find(
            ({ cmsLanguage }) => cmsLanguage === appLang
        );

        return [
            config,
            groups.filter(({ id }) =>
                config.groups.some((group) => group.id === id)
            )
        ];
    }, [languageConfigs]);

    const cookieName = "infracc" + configData.cookieConsentVersion;

    const consentCookie = cookies[cookieName];

    const [bannerActive, setBannerActive] = useState(false);

    const saveCookie = useCallback(
        (value) => {
            // remove the old cookie first
            removeCookie(cookieName);

            // expires after 1 year
            const expires = new Date();
            expires.setFullYear(new Date().getFullYear() + 1);

            setCookie(cookieName, value, {
                expires,
                path: "/"
                // secure: process.env.GATSBY_COOKIE_SECURE == true,
                // domain: process.env.GATSBY_COOKIE_DOMAIN,
                // sameSite:
                //     process.env.GATSBY_COOKIE_SECURE == true ? "strict" : false
            });
        },
        [cookieName]
    );

    /**
     * all keys in checkedSwitches indicate whether a cookie group is selected
     * [ "groupA_key", "groupC_key", "*" ]
     */
    const reducer = useCallback((state, action) => {
        switch (action.type) {
            case "accept all":
                // action: { type: 'accept all', keys: ['groupkey', "*", all available keys]}
                setBannerActive(false);
                setReopened(false);
                saveCookie({ groups: action.keys });
                return action.keys;
            case "decline":
                // action: { type: 'decline' }
                setBannerActive(false);
                setReopened(false);
                saveCookie({ groups: ["*"] });
                return ["*"];
            case "switch":
                // action: { type: 'switch', key: 'groupkey'}
                // if ("*" === action.key) return state;
                const newState = state.includes(action.key)
                    ? state.filter((ele) => ele !== action.key)
                    : state.concat(action.key);

                saveCookie({ groups: newState });

                return newState;
            case "save":
                setBannerActive(false);
                setReopened(false);
                return state;
            default:
                console.warn(`Unexpected switch type given: ${action.type}`);
                return state;
        }
    }, []);

    const [checkedSwitches, toggleSwitches] = useReducer(
        reducer,
        (consentCookie ?? { groups: ["*"] })?.groups
    ); // necessary (key = '*') must be always selected

    const fuctions = useMemo(
        () =>
            filteredGroups
                .filter(({ key }) => checkedSwitches.includes(key))
                .flatMap(({ functions }) => functions),
        [filteredGroups, checkedSwitches]
    );

    useEffect(() => {
        if (consentCookie) {
            return;
        }

        const callback = () => setBannerActive(true);

        window.addEventListener("scroll", callback, { once: true });
        return () => {
            window.removeEventListener("scroll", callback);
        };
    }, []);

    return (
        <CookieContext.Provider value={{ checkedSwitches, toggleSwitches }}>
            {(bannerActive || reopened) && (
                <CookieBanner
                    headline={configData?.headline}
                    intro={configData?.intro}
                    groups={filteredGroups}
                    settingsButton={configData?.settingsButton}
                    declineButton={configData?.declineButton}
                    acceptAllButton={configData?.acceptAllButton}
                    saveButton={configData?.saveButton}
                />
            )}
            <CookieFunktions functions={fuctions} />
        </CookieContext.Provider>
    );
}

const query = graphql`
    query {
        allDatoCmsLanguageConfiguration {
            nodes {
                id
                cmsLanguage
                headline: cookieConsentHeadline
                intro: cookieConsentIntro
                declineButton: cookieConsentNegBtnLabel
                cookieConsentPosBtnLabel
                acceptAllButton: cookieConsentPosallBtnLabel
                saveButton: cookieConsentSaveselBtnLabel
                settingsButton: cookieConsentSettingsBtnLabel
                cookieConsentVersion
                groups: cookieGroup {
                    id
                }
            }
        }
    }
`;
